import {
  CalendarOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  LockOutlined,
  PlayCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  disconnectQuickBooks,
  getQuickBooksAuthUrl,
  getQuickBooksTokenWithPassword,
  getQuickBooksConnectionStatus,
  QuickBooksTokenResponse,
} from "~/services/quickbooks/quickbooks.api";
import {
  useJobsStatus,
  useTriggerDailySnapshotsJob,
  useTriggerInvoiceSyncJob,
  useTriggerMonthlyInvoicesJob,
  useTriggerStorageCalculationsJob,
} from "~/services/storage/storage.queries";

const { Title, Text, Paragraph } = Typography;

// interface QuickBooksSettings {
//   clientId: string;
//   clientSecret: string;
//   redirectUri: string;
//   environment: "sandbox" | "production";
//   autoSync: boolean;
//   syncInterval: number;
// }

interface QuickBooksConnectionStatus {
  isConnected: boolean;
  companyName?: string;
  realmId?: string;
  lastSyncTime?: string;
  tokenExpiresAt?: string;
  error?: string;
  authUrl?: string;
}

// interface SyncHistory {
//   id: string;
//   type: "invoice" | "customer" | "item";
//   status: "success" | "failed" | "pending";
//   timestamp: string;
//   details: string;
// }

export const QuickBooksIntegration: React.FC = () => {
  const [connectionStatus, setConnectionStatus] =
    useState<QuickBooksConnectionStatus | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isGettingToken, setIsGettingToken] = useState(false);
  const [oauthToken, setOauthToken] = useState<QuickBooksTokenResponse | null>(
    null
  );
  const [form] = Form.useForm();
  // const [testResult, setTestResult] = useState<any>(null);
  // const [showTestModal, setShowTestModal] = useState(false);

  // Job management hooks
  const { data: jobsStatus, isLoading: jobsLoading } = useJobsStatus();
  const triggerDailySnapshots = useTriggerDailySnapshotsJob();
  const triggerMonthlyInvoices = useTriggerMonthlyInvoicesJob();
  const triggerInvoiceSync = useTriggerInvoiceSyncJob();
  const triggerStorageCalculations = useTriggerStorageCalculationsJob();

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setIsLoading(true);
      const status = await getQuickBooksConnectionStatus();
      setConnectionStatus(status);
    } catch (error: any) {
      message.error(error?.message || "Failed to load connection status");
      setConnectionStatus({
        isConnected: false,
        error: "Failed to load connection status",
        authUrl: "/api/quickbooks/auth/url",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      const authUrl = await getQuickBooksAuthUrl();

      // Open QuickBooks OAuth in a new window
      const authWindow = window.open(
        authUrl.authUrl,
        "quickbooks-auth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Listen for the auth completion
      const checkAuthComplete = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuthComplete);
            // Check connection status after auth window closes
            setTimeout(() => {
              loadConnectionStatus();
              setIsConnecting(false);
            }, 1000);
          }
        } catch (error) {
          // Handle cross-origin errors
          console.log("error", error);
        }
      }, 1000);
    } catch (error: any) {
      message.error(
        error?.message || "Failed to initiate QuickBooks connection"
      );
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectQuickBooks();
      setConnectionStatus({ isConnected: false });
      message.success("Successfully disconnected from QuickBooks");
    } catch (error: any) {
      message.error(error?.message || "Failed to disconnect from QuickBooks");
    }
  };

  const showPasswordModal = () => {
    setIsPasswordModalVisible(true);
    form.resetFields();
    setOauthToken(null);
  };

  const handlePasswordModalCancel = () => {
    setIsPasswordModalVisible(false);
    form.resetFields();
  };

  const handleGetOAuthToken = async () => {
    try {
      const values = await form.validateFields();
      setIsGettingToken(true);

      const token = await getQuickBooksTokenWithPassword(values.password);
      setOauthToken(token);
      message.success("OAuth token retrieved successfully");
    } catch (error: any) {
      if (error.errorFields) {
        return; // Form validation error
      }
      message.error(error?.message || "Failed to get OAuth token");
    } finally {
      setIsGettingToken(false);
    }
  };

  const handleTriggerJob = async (jobName: string, triggerFn: any) => {
    try {
      const result = await triggerFn.mutateAsync();
      message.success(
        `${jobName} job completed successfully: ${result.message}`
      );
    } catch (error: any) {
      message.error(
        `Failed to trigger ${jobName} job: ${error?.message || "Unknown error"}`
      );
    }
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "inactive":
        return "default";
      case "error":
        return "red";
      default:
        return "default";
    }
  };

  const jobColumns = [
    {
      title: "Job Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Space>
          <ClockCircleOutlined />
          <strong>{name}</strong>
        </Space>
      ),
    },
    {
      title: "Schedule",
      dataIndex: "schedule",
      key: "schedule",
      render: (schedule: string) => (
        <Tag icon={<CalendarOutlined />} color="blue">
          {schedule}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getJobStatusColor(status)}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Next Run",
      dataIndex: "nextRun",
      key: "nextRun",
      render: (nextRun: string) => (
        <Text type="secondary">{new Date(nextRun).toLocaleString()}</Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => {
        const jobTriggers = {
          "Daily Inventory Snapshots": () =>
            handleTriggerJob("Daily Snapshots", triggerDailySnapshots),
          "Monthly Storage Invoices": () =>
            handleTriggerJob("Monthly Invoices", triggerMonthlyInvoices),
          "Invoice Status Sync": () =>
            handleTriggerJob("Invoice Sync", triggerInvoiceSync),
          "Storage Calculations Update": () =>
            handleTriggerJob(
              "Storage Calculations",
              triggerStorageCalculations
            ),
        };

        const triggerJob = jobTriggers[record.name as keyof typeof jobTriggers];

        return triggerJob ? (
          <Tooltip title={`Manually trigger ${record.name}`}>
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={triggerJob}
              loading={
                (record.name === "Daily Inventory Snapshots" &&
                  triggerDailySnapshots.isPending) ||
                (record.name === "Monthly Storage Invoices" &&
                  triggerMonthlyInvoices.isPending) ||
                (record.name === "Invoice Status Sync" &&
                  triggerInvoiceSync.isPending) ||
                (record.name === "Storage Calculations Update" &&
                  triggerStorageCalculations.isPending)
              }
            >
              Run Now
            </Button>
          </Tooltip>
        ) : null;
      },
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading QuickBooks Integration...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <Title level={2}>
        <SettingOutlined style={{ marginRight: 8 }} />
        QuickBooks Integration
      </Title>
      <Paragraph>
        Manage your QuickBooks Online integration, view connection status, and
        configure automated jobs.
      </Paragraph>

      {/* Connection Status Card */}
      <Card
        title={
          <Space>
            <LinkOutlined />
            Connection Status
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {connectionStatus?.isConnected ? (
          <Alert
            message="QuickBooks Connected"
            description={
              <div>
                <div>
                  <strong>Company:</strong>{" "}
                  {connectionStatus.companyName || "Connected"}
                </div>
                {connectionStatus.lastSyncTime && (
                  <div>
                    <strong>Last Sync:</strong>{" "}
                    {new Date(connectionStatus.lastSyncTime).toLocaleString()}
                  </div>
                )}
                {connectionStatus.tokenExpiresAt && (
                  <div>
                    <strong>Token Expires:</strong>{" "}
                    {new Date(connectionStatus.tokenExpiresAt).toLocaleString()}
                  </div>
                )}
              </div>
            }
            type="success"
            showIcon
            action={
              <Space>
                <Button type="default" danger onClick={handleDisconnect}>
                  Disconnect
                </Button>
                <Button icon={<LockOutlined />} onClick={showPasswordModal}>
                  Get OAuth Token
                </Button>
              </Space>
            }
          />
        ) : (
          <Alert
            message="QuickBooks Not Connected"
            description={
              connectionStatus?.error ||
              "Connect to QuickBooks to enable invoice creation and synchronization."
            }
            type="warning"
            showIcon
            action={
              <Button
                type="primary"
                loading={isConnecting}
                onClick={handleConnect}
              >
                Connect to QuickBooks
              </Button>
            }
          />
        )}
      </Card>

      {/* Automated Jobs Section */}
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            Automated Jobs
            <Tooltip title="These jobs run automatically on schedule to maintain your storage billing and invoice synchronization">
              <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
            </Tooltip>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Paragraph>
          Monitor and manually trigger automated jobs for storage calculations,
          invoice processing, and data synchronization.
        </Paragraph>

        <Spin spinning={jobsLoading}>
          <Table
            dataSource={jobsStatus?.jobs || []}
            columns={jobColumns}
            rowKey="name"
            pagination={false}
            size="middle"
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ margin: 0, paddingLeft: 24 }}>
                  <Text>{record.description}</Text>
                  {record.lastRun && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        <strong>Last Run:</strong>{" "}
                        {new Date(record.lastRun).toLocaleString()}
                      </Text>
                    </div>
                  )}
                </div>
              ),
            }}
          />
        </Spin>

        <Divider />

        <Row gutter={16}>
          <Col span={24}>
            <Alert
              message="Job Schedule Information"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>
                    <strong>Daily Inventory Snapshots:</strong> Captures daily
                    inventory data at 1 AM for storage calculations
                  </li>
                  <li>
                    <strong>Monthly Storage Invoices:</strong> Processes storage
                    billing on the 1st of each month at 9 AM
                  </li>
                  <li>
                    <strong>Invoice Status Sync:</strong> Synchronizes invoice
                    payment status from QuickBooks every 6 hours
                  </li>
                  <li>
                    <strong>Storage Calculations Update:</strong> Updates
                    current month calculations daily at 11 PM
                  </li>
                </ul>
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>
      </Card>

      {/* Password Modal for OAuth Token */}
      <Modal
        title="Get OAuth Token"
        open={isPasswordModalVisible}
        onCancel={handlePasswordModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isGettingToken}
            onClick={handleGetOAuthToken}
          >
            Get Token
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="password"
            label="Admin Password"
            rules={[
              { required: true, message: "Please enter the admin password" },
            ]}
          >
            <Input.Password placeholder="Enter admin password" />
          </Form.Item>

          {oauthToken && (
            <>
              <div className="bg-gray-50 p-4 rounded-md mt-4">
                <h4 className="text-lg font-medium mb-2">
                  OAuth Token Information
                </h4>
                <div className="mb-2">
                  <div className="font-medium">Access Token:</div>
                  <Input.TextArea
                    value={oauthToken.access_token}
                    readOnly
                    rows={2}
                    className="mb-2"
                  />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.access_token);
                      message.success("Access token copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy access token
                  </Button>
                </div>

                <div className="mb-2">
                  <div className="font-medium">Refresh Token:</div>
                  <Input.TextArea
                    value={oauthToken.refresh_token}
                    readOnly
                    rows={2}
                    className="mb-2"
                  />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.refresh_token);
                      message.success("Refresh token copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy refresh token
                  </Button>
                </div>

                <div className="mb-2">
                  <div className="font-medium">Realm ID:</div>
                  <Input value={oauthToken.realmId} readOnly className="mb-2" />
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(oauthToken.realmId || "");
                      message.success("Realm ID copied to clipboard");
                    }}
                    style={{ padding: 0 }}
                  >
                    Copy Realm ID
                  </Button>
                </div>

                <div>
                  <div className="font-medium">Token Details:</div>
                  <ul className="list-disc pl-5">
                    <li>Token Type: {oauthToken.token_type}</li>
                    <li>Expires In: {oauthToken.expires_in} seconds</li>
                    <li>
                      Refresh Token Expires In:{" "}
                      {oauthToken.x_refresh_token_expires_in} seconds
                    </li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default QuickBooksIntegration;
